// src/components/settings/ProjectSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  Users,
  UserPlus,
  UserMinus,
  Search,
  Building,
  MapPin,
  School,
  Mail,
  Phone,
  Loader2,
  Settings,
  Info,
  FileText,
  Calendar,
  DollarSign,
  Target,
  Plus,
  Minus,
  Upload,
  Image,
  Trash2
} from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUniqueProvinces, 
  getDistrictsByProvince, 
  getSchoolsByFilters 
} from '../../datalists/Schools';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../services/firebase';

const ProjectSettings = ({ project, onClose, onSave }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // General settings state - includes ALL current fields
  const [projectData, setProjectData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    province: project?.province || '',
    district: project?.district || '',
    circuit: project?.circuit || '',
    projectLevel: project?.projectLevel || 'District',
    status: project?.status || 'active',
    startDate: project?.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
    endDate: project?.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
    objectives: project?.objectives || [''],
    budget: project?.budget || '',
    contactPerson: project?.contactPerson || '',
    contactEmail: project?.contactEmail || '',
    contactPhone: project?.contactPhone || '',
    memberSchools: project?.memberSchools || [],
    logoUrl: project?.logoUrl || '',
    logoFile: null
  });

  // Schools management state
  const [provinces] = useState(getUniqueProvinces());
  const [districts, setDistricts] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');

  // Member management state
  const [participants, setParticipants] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'schools', label: 'Schools', icon: School },
    { id: 'info', label: 'Info', icon: Info }
  ];

  const projectLevels = ['Provincial', 'District', 'Circuit', 'School'];

  useEffect(() => {
    if (project) {
      loadParticipants();
      loadDistricts();
      loadAvailableSchools();
    }
  }, [project]);

  useEffect(() => {
    if (activeTab === 'members' && projectData) {
      searchUsers();
    }
  }, [activeTab, userSearchTerm, projectData]);

  // Update districts when province changes
  useEffect(() => {
    loadDistricts();
  }, [projectData.province]);

  // Load available schools when location changes
  useEffect(() => {
    loadAvailableSchools();
  }, [projectData.province, projectData.district, projectData.circuit]);

  const loadDistricts = () => {
    if (projectData.province) {
      const provinceDistricts = getDistrictsByProvince(projectData.province);
      setDistricts(provinceDistricts);
    } else {
      setDistricts([]);
    }
  };

  const loadAvailableSchools = () => {
    if (projectData.province && projectData.district) {
      const filters = {
        province: projectData.province,
        district: projectData.district
      };
      
      if (projectData.circuit) {
        filters.circuit = projectData.circuit;
      }

      const schools = getSchoolsByFilters(filters);
      setAvailableSchools(schools);
    } else {
      setAvailableSchools([]);
    }
  };

  const loadParticipants = async () => {
    if (!project?.id) return;
    
    try {
      setLoadingParticipants(true);
      const participantDetails = await ProjectService.getProjectParticipantsDetails(project.id);
      setParticipants(participantDetails);
    } catch (err) {
      console.error('Error loading participants:', err);
      setError('Failed to load project members');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const searchUsers = async () => {
    try {
      setSearchingUsers(true);
      const users = await ProjectService.searchUsersByLocation(
        { ...projectData, participants: participants.map(p => p.id) },
        userSearchTerm
      );
      setAvailableUsers(users);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users');
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProjectData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleObjectiveChange = (index, value) => {
    const newObjectives = [...projectData.objectives];
    newObjectives[index] = value;
    setProjectData(prev => ({
      ...prev,
      objectives: newObjectives
    }));
  };

  const addObjective = () => {
    setProjectData(prev => ({
      ...prev,
      objectives: [...prev.objectives, '']
    }));
  };

  const removeObjective = (index) => {
    if (projectData.objectives.length > 1) {
      setProjectData(prev => ({
        ...prev,
        objectives: prev.objectives.filter((_, i) => i !== index)
      }));
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setError('File size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');

      // Create unique filename
      const filename = `project-logos/${project.id}_${Date.now()}_${file.name}`;
      const logoRef = ref(storage, filename);

      // Upload file
      await uploadBytes(logoRef, file);
      const logoUrl = await getDownloadURL(logoRef);

      // Delete old logo if it exists
      if (projectData.logoUrl) {
        try {
          const oldLogoRef = ref(storage, projectData.logoUrl);
          await deleteObject(oldLogoRef);
        } catch (error) {
          console.log('Could not delete old logo:', error);
        }
      }

      setProjectData(prev => ({
        ...prev,
        logoUrl: logoUrl
      }));

      setSuccess('Logo uploaded successfully');
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (!projectData.logoUrl) return;

    try {
      setLoading(true);
      const logoRef = ref(storage, projectData.logoUrl);
      await deleteObject(logoRef);
      
      setProjectData(prev => ({
        ...prev,
        logoUrl: ''
      }));
      
      setSuccess('Logo removed successfully');
    } catch (err) {
      console.error('Error removing logo:', err);
      setError('Failed to remove logo');
    } finally {
      setLoading(false);
    }
  };

  const addMemberSchool = (school) => {
    if (!projectData.memberSchools.find(ms => ms.schoolName === school.schoolName)) {
      setProjectData(prev => ({
        ...prev,
        memberSchools: [...prev.memberSchools, {
          schoolName: school.schoolName,
          province: school.province,
          district: school.district,
          circuit: school.circuit || '',
          phase: school.phase || '',
          addedAt: new Date()
        }]
      }));
    }
    setSchoolSearchTerm('');
  };

  const removeMemberSchool = (index) => {
    setProjectData(prev => ({
      ...prev,
      memberSchools: prev.memberSchools.filter((_, i) => i !== index)
    }));
  };

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      setLoading(true);
      
      for (const userId of selectedUsers) {
        await ProjectService.addParticipantToProject(project.id, userId);
      }

      setSuccess(`Successfully added ${selectedUsers.length} member(s) to the project`);
      setSelectedUsers([]);
      await loadParticipants();
      await searchUsers();
      
    } catch (err) {
      console.error('Error adding members:', err);
      setError('Failed to add members to project');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the project?')) {
      return;
    }

    try {
      setLoading(true);
      await ProjectService.removeParticipantFromProject(project.id, userId);
      setSuccess('Member removed successfully');
      await loadParticipants();
      await searchUsers();
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      setLoading(true);
      
      const updatedData = {
        ...projectData,
        objectives: projectData.objectives.filter(obj => obj.trim()),
        startDate: projectData.startDate ? new Date(projectData.startDate) : null,
        endDate: projectData.endDate ? new Date(projectData.endDate) : null,
        budget: projectData.budget ? parseFloat(projectData.budget) : null,
      };

      // Remove logoFile from data sent to Firestore
      delete updatedData.logoFile;

      await ProjectService.updateProject(project.id, updatedData);
      setSuccess('Project settings updated successfully');
      if (onSave) onSave();
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredAvailableSchools = availableSchools.filter(school => 
    school.schoolName.toLowerCase().includes(schoolSearchTerm.toLowerCase()) &&
    !projectData.memberSchools.find(ms => ms.schoolName === school.schoolName)
  );

  const renderGeneralTab = () => (
    <div className="space-y-8">
      {/* Project Logo Section */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
          <Image className="w-4 h-4" />
          Project Logo
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1">
            {projectData.logoUrl ? (
              <div className="relative">
                <img 
                  src={projectData.logoUrl} 
                  alt="Project Logo" 
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={removeLogo}
                  disabled={loading}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No logo uploaded</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="lg:col-span-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
            />
            <p className="text-xs text-gray-500 mt-2">
              Upload a logo for your project. Recommended: 400x200px
            </p>
            {uploadingLogo && (
              <p className="text-sm text-blue-600 mt-2">Uploading logo...</p>
            )}
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Basic Information
        </h4>
        
        {/* Row 1: Project Name, Level, and Status */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Name *
            </label>
            <input
              type="text"
              name="name"
              value={projectData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter project name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Level *
            </label>
            <select
              name="projectLevel"
              value={projectData.projectLevel}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {projectLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={projectData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Row 2: Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={projectData.description}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter project description"
          />
        </div>
      </div>

      {/* Location Information */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Location Information
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Province *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                name="province"
                value={projectData.province}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select province</option>
                {provinces.map(province => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              District *
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                name="district"
                value={projectData.district}
                onChange={handleChange}
                disabled={!projectData.province}
                required
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
              >
                <option value="">Select district</option>
                {districts.map(district => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>
          </div>

          {(projectData.projectLevel === 'Circuit' || projectData.projectLevel === 'School') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Circuit
              </label>
              <input
                type="text"
                name="circuit"
                value={projectData.circuit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter circuit"
              />
            </div>
          )}
        </div>
      </div>

      {/* Project Timeline */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Project Timeline
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={projectData.startDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={projectData.endDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Project Objectives */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
          <Target className="w-4 h-4" />
          Project Objectives
        </h4>
        
        <div className="space-y-3">
          {projectData.objectives.map((objective, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={objective}
                onChange={(e) => handleObjectiveChange(index, e.target.value)}
                placeholder={`Objective ${index + 1}`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {projectData.objectives.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeObjective(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addObjective}
            className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Objective
          </button>
        </div>
      </div>

      {/* Budget and Contact Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Budget */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Budget
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Amount (R)
            </label>
            <input
              type="number"
              name="budget"
              value={projectData.budget}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Contact Information
          </h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person *
              </label>
              <input
                type="text"
                name="contactPerson"
                value={projectData.contactPerson}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter contact person name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  name="contactEmail"
                  value={projectData.contactEmail}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="contact@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  name="contactPhone"
                  value={projectData.contactPhone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveGeneral}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>
    </div>
  );

  const renderMembersTab = () => (
    <div className="space-y-6">
      {/* Current Members */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">Current Members</h4>
          <div className="text-sm text-gray-500">
            {participants.length} member{participants.length !== 1 ? 's' : ''}
          </div>
        </div>

        {loadingParticipants ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading members...</span>
          </div>
        ) : participants.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No members in this project yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {participants.map(member => (
              <div key={member.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {member.title} {member.firstName} {member.lastName}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-4">
                        <span>{member.position}</span>
                        {member.schoolName && (
                          <span className="flex items-center gap-1">
                            <School className="w-3 h-3" />
                            {member.schoolName}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-4">
                        {member.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {member.email}
                          </span>
                        )}
                        {member.mobileNumber && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {member.mobileNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {project.createdBy === currentUser?.uid && member.id !== currentUser?.uid && (
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={loading}
                    className="flex items-center gap-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                  >
                    <UserMinus className="w-4 h-4" />
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Members */}
      {project.createdBy === currentUser?.uid && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">Add Members</h4>
            <div className="text-sm text-gray-500">
              Level: {projectData.projectLevel}
            </div>
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                placeholder={`Search users in ${projectData.projectLevel.toLowerCase()} level...`}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {selectedUsers.length > 0 && (
            <div className="mb-4 flex items-center justify-between p-3 bg-blue-50 rounded-md">
              <span className="text-blue-800">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleAddMembers}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Add Members
              </button>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {searchingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Searching users...</span>
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {userSearchTerm ? 'No users found matching your search' : 'No available users at this level'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableUsers.map(user => {
                  const isSelected = selectedUsers.includes(user.id);
                  return (
                    <div 
                      key={user.id} 
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleUserSelection(user.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {user.title} {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.position} • {user.schoolName || user.district}
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderSchoolsTab = () => (
    <div className="space-y-6">
      {/* Current Member Schools */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">Member Schools</h4>
          <div className="text-sm text-gray-500">
            {projectData.memberSchools.length} school{projectData.memberSchools.length !== 1 ? 's' : ''}
          </div>
        </div>

        {projectData.memberSchools.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <School className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No member schools added yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {projectData.memberSchools.map((school, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{school.schoolName}</div>
                  <div className="text-sm text-gray-500">
                    {school.circuit && `${school.circuit}, `}{school.phase}
                  </div>
                  <div className="text-xs text-gray-400">
                    {school.district}, {school.province}
                  </div>
                </div>
                {project.createdBy === currentUser?.uid && (
                  <button
                    onClick={() => removeMemberSchool(index)}
                    className="text-red-600 hover:bg-red-50 p-1 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Schools */}
      {project.createdBy === currentUser?.uid && availableSchools.length > 0 && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Add Schools</h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add Schools from {projectData.district}, {projectData.province}
            </label>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search schools..."
                value={schoolSearchTerm}
                onChange={(e) => setSchoolSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {filteredAvailableSchools.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {filteredAvailableSchools.slice(0, 10).map((school, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => addMemberSchool(school)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{school.schoolName}</div>
                    <div className="text-sm text-gray-500">
                      {school.circuit ? `${school.circuit}, ` : ''}{school.phase}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderInfoTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-6">
        <h4 className="text-lg font-medium text-blue-900 mb-4">Project Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-800">Level:</span>
            <span className="ml-2 text-blue-700">{project.projectLevel}</span>
          </div>
          <div>
            <span className="font-medium text-blue-800">Status:</span>
            <span className="ml-2 text-blue-700 capitalize">{project.status}</span>
          </div>
          <div>
            <span className="font-medium text-blue-800">Created:</span>
            <span className="ml-2 text-blue-700">
              {project.createdAt ? new Date(project.createdAt).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800">Last Updated:</span>
            <span className="ml-2 text-blue-700">
              {project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800">Members:</span>
            <span className="ml-2 text-blue-700">{participants.length}</span>
          </div>
          <div>
            <span className="font-medium text-blue-800">Schools:</span>
            <span className="ml-2 text-blue-700">{projectData.memberSchools.length}</span>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 rounded-lg p-6">
        <h4 className="text-lg font-medium text-yellow-900 mb-4">Member Search Scope</h4>
        <div className="text-sm text-yellow-800">
          <p className="mb-2">Based on your project level, members can be added from:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            {projectData.projectLevel === 'Provincial' && (
              <li>All users in {projectData.province} province</li>
            )}
            {projectData.projectLevel === 'District' && (
              <>
                <li>All users in {projectData.district} district</li>
                <li>Province: {projectData.province}</li>
              </>
            )}
            {projectData.projectLevel === 'Circuit' && (
              <>
                <li>All users in {projectData.circuit} circuit</li>
                <li>District: {projectData.district}</li>
                <li>Province: {projectData.province}</li>
              </>
            )}
            {projectData.projectLevel === 'School' && (
              <>
                <li>Users from member schools only</li>
                <li>Circuit: {projectData.circuit}</li>
                <li>District: {projectData.district}</li>
                <li>Province: {projectData.province}</li>
              </>
            )}
          </ul>
        </div>
      </div>

      {projectData.objectives.filter(obj => obj.trim()).length > 0 && (
        <div className="bg-green-50 rounded-lg p-6">
          <h4 className="text-lg font-medium text-green-900 mb-4">Project Objectives</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
            {projectData.objectives.filter(obj => obj.trim()).map((objective, index) => (
              <li key={index}>{objective}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Project Settings</h2>
            <p className="text-sm text-gray-500 mt-1">{project?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <button 
              onClick={() => setError('')}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-700">{success}</span>
            <button 
              onClick={() => setSuccess('')}
              className="ml-auto text-green-500 hover:text-green-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'members' && renderMembersTab()}
          {activeTab === 'schools' && renderSchoolsTab()}
          {activeTab === 'info' && renderInfoTab()}
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;